import React, { useMemo, useRef, useState } from "react";
import { I, T } from "services/lang/LanguageService";
import { Dashboard } from "api/model/dashboard/Dashboard";
import {
  easasErrorAlert,
  easasSuccessAlert,
} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useUpdateDashboard } from "api/api-hooks/dashboards-hooks";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import CreateDatasetDialog from "components/dashboard/dataset-window/datasets-menu/create-dataset-dialog/CreateDatasetDialog";
import useTabId from "hooks/dashboard/useTabId";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import { Link, useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import useDashboardStore from "store/dashboard-store";
import { ContextTypes } from "api/model/dashboard/DashboardTab";
import "../dashboard-styles/DashboardMain.scss";
import DashboardTabConfigurator from "./tab/DashboardTabConfigurator";
import TabContextMenu from "./tab/TabContextMenu";
import TabTitleEditor from "./tab/TabTitleEditor";
import AddNewTabGroup from "./tab/AddNewTabGroup";
import DashboardEditor from "./DashboardEditor";
import ImportDashboardOrTab, { handleAddNewTab } from "./ImportDashboardOrTab";
import { ReorderType } from "./tab/reorder/DraggableItem";
import Reorder from "./tab/reorder/Reorder";

export interface ICurrentDashboard {
  dashboard: Dashboard;
  isEditMode: boolean;
  setCurrentDashboard: (dashboard: Dashboard) => void;
}

const DashboardMain = () => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const setIsEditMode = useDashboardStore(state => state.setIsEditMode);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const tabId = useTabId();
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);

  const navigate = useNavigate();

  const [isChangeTabTitleVisible, setIsChangeTabTitleVisible] = useState<boolean>(false);
  const [isImportDashboardDialogVisible, setIsImportDashboardDialogVisibility] =
    useState<boolean>(false);
  const [selectedTabTitle, setSelectedTabTitle] = useState<string>("");
  const [reorderTabsDialogVisibility, setReorderTabsDialogVisibility] = useState<boolean>(false);
  const [addNewGroupDialogVisibility, setAddNewGroupDialogVisibility] = useState<boolean>(false);
  const [tabConfiguratorvisibility, setTabConfiguratorVisibility] = useState<boolean>(false);
  const [isCreateDatasetDialogVisible, setIsCreateDatasetDialogVisible] = useState<boolean>(false);

  const updateQuery = useUpdateDashboard({
    onSuccess: () => {
      easasSuccessAlert(T("alert.dashboard-updated"));
    },
    onFailure: (error: Error) => {
      easasErrorAlert(error.message);
    },
  });

  const menuRef = useRef<Menu>(null);

  const menuItems = useMemo(() => {
    return dashboard?.dashboardTabsJs?.map((tab, index) => ({
      label: tab.title,
      command: () => onTabChange(index),
    }));
  }, [dashboard]);

  if (!dashboard || !dashboard.dashboardTabsJs) {
    return <ProgressSpinner />;
  }

  if (dashboard.dashboardTabsJs.length === 0) {
    handleAddNewTab(dashboard, setDashboard, T("word.new-tab"));
  }

  const currentTab = dashboard?.dashboardTabsJs?.[tabId];
  const currentTabContextType = currentTab?.contextConfig?.contextType || ContextTypes.NONE;

  const onTabChange = (index: number) => {
    if (index !== tabId) {
      navigate(`/dashboard/${dashboard?.id}/tab/${index}`);
    }
  };

  const handleEditMode = (e: InputSwitchChangeParams) => {
    if (!e.value) {
      setIsGroupReorderEnabled(e.value);
    }
    setIsEditMode(e.value);
  };

  const triggerTabTitleChangeDialog = () => {
    setSelectedTabTitle(dashboard.dashboardTabsJs[tabId].title);
    setIsChangeTabTitleVisible(true);
  };

  return (
    <>
      <div className="flex flex-row justify-content-between mb-3">
        <div>{I(dashboard?.title)}</div>
        <div className="flex flex-row gap-2">
          <div>{T("word.options")}:</div>
          <InputSwitch checked={isEditMode} onChange={e => handleEditMode(e)} />
        </div>
      </div>
      {isEditMode && (
        <DashboardEditor
          updateQuery={updateQuery}
          dashboard={dashboard}
          setDashboard={setDashboard}
          tabId={tabId}
          triggerTabTitleChangeDialog={triggerTabTitleChangeDialog}
          setReorderTabsDialogVisibility={setReorderTabsDialogVisibility}
          setTabConfiguratorVisibility={setTabConfiguratorVisibility}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          isGroupReorderEnabled={isGroupReorderEnabled}
          setIsGroupReorderEnabled={setIsGroupReorderEnabled}
          setIsCreateDatasetDialogVisible={setIsCreateDatasetDialogVisible}
        />
      )}
      <div>
        <div className="tabsFlex">
          {dashboard?.dashboardTabsJs?.map((tab, index) => (
            <Link
              key={tab.uuid}
              to={`/dashboard/${dashboard.id}/tab/${index}`}
              className={`tab dashboard-tab ${
                tabId !== undefined && tabId === index ? "tab-selected" : "tab-unselected"
              }`}
            >
              {tab.title}
            </Link>
          ))}
        </div>
        <div className="mobile-menu">
          <Button
            onClick={event => menuRef.current?.toggle(event)}
            className="p-button-icon-only mb-3 w-12"
          >
            <i className="pi pi-bars mr-2 text-2xl" />
            <div className="font-bold text-xl">{T("word.tabs")}</div>
          </Button>
          <Menu ref={menuRef} model={menuItems} popup className="w-12 menu-list" />
        </div>
      </div>
      {currentTabContextType && currentTabContextType !== ContextTypes.NONE && (
        <TabContextMenu tabId={tabId} />
      )}
      {isChangeTabTitleVisible && (
        <TabTitleEditor
          isChangeTabTitleVisible={isChangeTabTitleVisible}
          setIsChangeTabTitleVisible={setIsChangeTabTitleVisible}
          selectedTabTitle={selectedTabTitle}
          setSelectedTabTitle={setSelectedTabTitle}
          dashboard={dashboard}
          setDashboard={setDashboard}
          tabId={tabId}
        />
      )}
      {isImportDashboardDialogVisible && (
        <ImportDashboardOrTab
          isImportDashboardDialogVisible={isImportDashboardDialogVisible}
          setIsImportDashboardDialogVisibility={setIsImportDashboardDialogVisibility}
          dashboard={dashboard}
        />
      )}
      {tabConfiguratorvisibility && (
        <DashboardTabConfigurator
          onHide={() => setTabConfiguratorVisibility(false)}
          currentContextType={currentTabContextType}
          tabId={tabId}
          dashboard={dashboard}
          setDashboard={setDashboard}
        />
      )}
      {addNewGroupDialogVisibility && (
        <AddNewTabGroup
          dashboard={dashboard}
          addNewGroupDialogVisibility={addNewGroupDialogVisibility}
          setAddNewGroupDialogVisibility={setAddNewGroupDialogVisibility}
          tabId={tabId}
          updateQuery={updateQuery}
        />
      )}
      {reorderTabsDialogVisibility && (
        <Reorder
          onChange={onTabChange}
          setReorderDialogVisibility={setReorderTabsDialogVisibility}
          reorderDialogVisibility={reorderTabsDialogVisibility}
          reorderType={ReorderType.TABS}
        />
      )}
      {isCreateDatasetDialogVisible && (
        <Dialog
          header={T("action.create-dataset")}
          visible={isCreateDatasetDialogVisible}
          onHide={() => setIsCreateDatasetDialogVisible(false)}
          style={{ width: "50vw" }}
          modal={false}
        >
          <CreateDatasetDialog setCreateDialogVisibility={setIsCreateDatasetDialogVisible} />
        </Dialog>
      )}
    </>
  );
};

export default DashboardMain;
